/**
 * analyzeImageEvidence — Image_Intelligence_Analyzer IIA-1 (row 0.99253).
 *
 * Gemini multimodal extractor for IMAGE evidence (skeleton). Mirrors the
 * shape + cost guards of `extractFromDocument.ts` (PDF/text extraction)
 * and sits adjacent to `docClassifier.ts` (text-document classifier).
 *
 * Scope this turn — IIA-1 only:
 *   - single-image classification (`classifyImageKind`),
 *   - single-image findings extraction (`analyzeImageEvidence`),
 *   - 9 image kinds (site-photo, aerial, kmz-overlay, flood-map,
 *     survey-plat, one-line-diagram, protest-flyer, permit-stamp, other),
 *   - conservative low-confidence pass-through to `{ kind: 'other',
 *     findings: [] }` when the classifier is not at least 0.6 sure,
 *   - injectable Gemini runner so unit tests pin a deterministic
 *     response shape without mocking the SDK.
 *
 * Deferred to IIA-2+:
 *   - library-wide scan (batch over all SiteDocument images),
 *   - Coverage Matrix wiring (calling this from `coverageMatrix.ts`
 *     when a slot has an image-bearing supporting doc),
 *   - feeding `ImageFinding.evidenceQuote` into the SED slot's
 *     `SedSlotProvenance` chain (PRE-1 pre-built that chain; this
 *     module returns the right shape but doesn't write it yet).
 *
 * Design notes:
 *   - Multimodal call shape mirrors `geminiService.describeImageForSite`
 *     (the only other inline-image call in the repo): `parts: [{
 *     inlineData: { data, mimeType } }, { text: prompt }]` with model
 *     `gemini-2.5-flash` (the cheap, vision-capable model). We do NOT
 *     route through `runGeminiJSON`, which is text-only.
 *   - Cost guards parallel `extractFromDocument`: per-call timeout
 *     (30 s default), strict closed-vocabulary kind enum, conservative
 *     confidence floor, never-throws-to-caller behaviour.
 *   - Two-stage flow: classify first (cheap), then extract per-kind
 *     findings (still on flash). Classification is what gates whether
 *     we trust the kind label at all — a one-line-diagram emitted
 *     against a 0.42 confidence is worse than `other / []`.
 */
import type { Schema } from '../geminiClient';
import { Type, ai } from '../geminiClient';

/* ── Image kind vocabulary ────────────────────────────────────────── */

/**
 * Closed vocabulary of image kinds IIA understands. Mirrors the kinds
 * the prompt-pack spec calls out + an `'other'` escape hatch for
 * everything outside the vocabulary. The vocabulary is intentionally
 * narrow this turn — adding a new kind requires:
 *   1. extending this union,
 *   2. extending `IMAGE_KIND_VALUES` (used as the Gemini enum),
 *   3. adding a per-kind extraction schema in `KIND_SCHEMAS`,
 *   4. covering the new kind in the regression test.
 *
 * The kinds chosen for IIA-1:
 *   - 'site-photo'        — ground-level photograph of the parcel.
 *   - 'aerial'            — top-down satellite or drone shot.
 *   - 'kmz-overlay'       — KMZ / KML map overlay (boundaries, layers).
 *   - 'flood-map'         — FEMA FIRM / NFHL flood-zone map crop.
 *   - 'survey-plat'       — recorded survey plat / boundary survey.
 *   - 'one-line-diagram'  — electrical one-line / single-line diagram.
 *   - 'protest-flyer'     — community-protest leaflet (textual evidence
 *                           of public opposition; routed to a separate
 *                           IIA-2+ ingest path, marked here only).
 *   - 'permit-stamp'      — agency permit stamp / approval seal.
 *   - 'other'             — anything else (no findings extracted).
 */
export type ImageKind =
  | 'site-photo'
  | 'aerial'
  | 'kmz-overlay'
  | 'flood-map'
  | 'survey-plat'
  | 'one-line-diagram'
  | 'protest-flyer'
  | 'permit-stamp'
  // IIA-2 — geotechnical borehole log (USCS class, bearing capacity,
  // depth-to-groundwater, per-layer description). Routed by the
  // per-kind extractor in `imageExtractors.ts` to the `geotech`
  // requirement.
  | 'borehole-log'
  // DIM-1 — an image OF a document: screenshot, scan, or photograph of
  // a spreadsheet, table, letter, form, report page, or similar with
  // dense readable text. Previously these collapsed to 'other' (the
  // vocabulary had no slot), which the dispatcher then recorded as
  // `attemptResult: 'extraction-failed'` even when the OCR pass
  // succeeded. No structured extractor — routes to the free-text
  // observation path, which carries the transcription.
  | 'document-image'
  | 'other';

/**
 * Enum-style list of every member of `ImageKind`. Kept in sync with the
 * union by the regression test (any new member of `ImageKind` not added
 * here causes a TypeScript compile error in `KIND_SCHEMAS` reference).
 */
export const IMAGE_KIND_VALUES: readonly ImageKind[] = [
  'site-photo',
  'aerial',
  'kmz-overlay',
  'flood-map',
  'survey-plat',
  'one-line-diagram',
  'protest-flyer',
  'permit-stamp',
  'borehole-log',
  'document-image',
  'other',
] as const;

/**
 * Conservative classifier floor. When the classifier reports a
 * confidence below this we drop the kind label and return
 * `{ kind: 'other', findings: [] }` — fabricating a label is worse than
 * admitting uncertainty (the user can re-tag manually).
 */
export const MIN_KIND_CONFIDENCE = 0.6;

/* ── Result types ─────────────────────────────────────────────────── */

/**
 * One extracted fact tied to a region of the image.
 *
 * `fieldPath` is the dotted SED slot path (e.g. `'parcel.acres'`,
 * `'flood.floodZone'`) the value belongs against. Keeping it as a free
 * string this turn means IIA-1 ships without a hard coupling to the
 * SedSlotId union — downstream wiring (IIA-2+) will refine the type
 * once the Coverage Matrix call sites materialise.
 *
 * `evidenceQuote` carries the OCR'd / inferred text snippet the model
 * grounded the value in. This is the field that will eventually flow
 * into `SedSlotProvenance.evidenceQuote` once IIA wires into the
 * resolver chain. The type is ready; the integration is deferred.
 *
 * `pixelRegion` is the bounding box (in image pixels) that the finding
 * was sourced from. Optional — the model is permitted to omit it when
 * it cannot localise the source confidently. Used by the eventual
 * image-viewer overlay (deferred) to highlight what the model "saw".
 */
export interface ImageFinding {
  fieldPath: string;
  value: unknown;
  confidence: number;
  evidenceQuote: string;
  pixelRegion?: { x: number; y: number; w: number; h: number };
}

/**
 * Return shape from a successful (or conservative-fallthrough) image
 * analysis call. `kind` + `kindConfidence` reflect the classifier
 * pass; `findings` reflect the per-kind extraction pass.
 *
 * When `kindConfidence < MIN_KIND_CONFIDENCE` the call returns
 * `{ kind: 'other', kindConfidence: <reported>, findings: [] }` and
 * `extractedText` is omitted — we don't trust the OCR pass either if
 * the classifier didn't know what the image was.
 */
export interface ImageAnalysisResult {
  kind: ImageKind;
  kindConfidence: number;
  findings: ImageFinding[];
  /** Raw OCR text the model recovered, when applicable. Omitted on
   *  low-confidence falls-through. Capped at 4 000 chars by the
   *  result-trimmer; longer transcripts are truncated. */
  extractedText?: string;
}

/* ── Gemini runner injection points ──────────────────────────────── */

/**
 * Shape of a Gemini multimodal call result, narrowed to the keys
 * IIA-1 reads. Matches the @google/genai SDK's `generateContent`
 * return value for both the AI Studio and Vertex backends.
 */
interface MultimodalGeminiResponse {
  text: string | (() => string);
}

/**
 * Multimodal runner type — the function `analyzeImageEvidence` and
 * `classifyImageKind` call to talk to Gemini. Injectable so unit tests
 * can pin a deterministic response without mocking the @google/genai
 * SDK. The default runner (`defaultMultimodalRunner`) wraps the same
 * `ai.models.generateContent` call site that `describeImageForSite`
 * uses, with a 30 s timeout.
 */
export type ImageGeminiRunner = (args: {
  base64Data: string;
  mimeType: string;
  prompt: string;
  schema: Schema;
  timeoutMs?: number;
  model?: string;
}) => Promise<unknown>;

/**
 * Default multimodal Gemini runner. Mirrors the call shape used by
 * `describeImageForSite` (model `gemini-2.5-flash`, parts =
 * [{ inlineData }, { text }]) plus structured-JSON enforcement
 * (`responseMimeType: 'application/json'`, `responseSchema`).
 *
 * Per-call timeout caps Gemini cost on a hung connection. Returns the
 * parsed JSON object on success, throws on timeout / empty / non-JSON.
 */
export const defaultMultimodalRunner: ImageGeminiRunner = async ({
  base64Data,
  mimeType,
  prompt,
  schema,
  timeoutMs = 30_000,
  model = 'gemini-2.5-flash',
}) => {
  const callPromise = ai.models.generateContent({
    model,
    contents: [
      {
        role: 'user',
        parts: [{ inlineData: { data: base64Data, mimeType } }, { text: prompt }],
      },
    ],
    config: {
      responseMimeType: 'application/json',
      responseSchema: schema,
    },
  });

  const response = (await Promise.race([
    callPromise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(`Gemini multimodal call timed out after ${timeoutMs}ms`)), timeoutMs),
    ),
  ])) as MultimodalGeminiResponse;

  const text = typeof response.text === 'function' ? response.text() : response.text;
  if (!text) throw new Error('Gemini multimodal call returned empty response');
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Gemini multimodal call returned invalid JSON: ${String(text).slice(0, 200)}`);
  }
};

/* ── Schemas ──────────────────────────────────────────────────────── */

/**
 * Schema for the classifier pass — Gemini must pick one of the known
 * kinds and return its confidence. Closed enum prevents the model from
 * inventing a new kind label ("hand-sketch", "screenshot") that the
 * rest of the pipeline doesn't know how to route.
 */
const CLASSIFIER_SCHEMA: Schema = {
  type: Type.OBJECT,
  properties: {
    kind: {
      type: Type.STRING,
      // Enum field present on the @google/genai Schema; the SDK enforces
      // it server-side so the response is guaranteed to be a member.
      enum: [...IMAGE_KIND_VALUES],
    },
    confidence: { type: Type.NUMBER },
  },
  required: ['kind', 'confidence'],
};

/**
 * Schema for one ImageFinding row in the extraction response. Kept
 * narrow so a malformed model response (extra keys, wrong types) is
 * rejected by `responseSchema` validation server-side.
 *
 * `pixelRegion` is OPTIONAL — the model is allowed to omit it. We
 * defensively drop it at projection time when the values don't add up
 * (negative dimensions, NaN coordinates).
 */
const FINDING_SCHEMA: Schema = {
  type: Type.OBJECT,
  properties: {
    fieldPath: { type: Type.STRING },
    value: { type: Type.STRING }, // serialised; we deserialise leniently at projection.
    confidence: { type: Type.NUMBER },
    evidenceQuote: { type: Type.STRING },
    pixelRegion: {
      type: Type.OBJECT,
      properties: {
        x: { type: Type.NUMBER },
        y: { type: Type.NUMBER },
        w: { type: Type.NUMBER },
        h: { type: Type.NUMBER },
      },
      required: ['x', 'y', 'w', 'h'],
    },
  },
  required: ['fieldPath', 'value', 'confidence', 'evidenceQuote'],
};

/**
 * Schema for the extraction pass response. Mirrors `ImageAnalysisResult`
 * minus the classifier kind/confidence fields, which the extractor
 * pass doesn't re-emit (we carry them through from the classifier).
 */
const EXTRACTION_RESPONSE_SCHEMA: Schema = {
  type: Type.OBJECT,
  properties: {
    findings: { type: Type.ARRAY, items: FINDING_SCHEMA },
    extractedText: { type: Type.STRING },
  },
  required: ['findings'],
};

/* ── Per-kind extraction prompts ──────────────────────────────────── */

/**
 * Short, kind-specific instruction blocks that hint Gemini at what
 * field-paths to emit. Each entry is appended to the shared extraction
 * preamble. Keeping these here (rather than free-form inside the
 * extractor) means new contributors can extend the analyzer by editing
 * one map.
 *
 * `'other'` is intentionally absent — analyzeImageEvidence never calls
 * Gemini when the classifier returned `'other'`, so a prompt for it
 * would be unreachable.
 */
const KIND_EXTRACTION_HINTS: Record<Exclude<ImageKind, 'other'>, string> = {
  'site-photo':
    "This is a ground-level photograph of the project parcel. Emit findings ONLY for what's plainly " +
    "visible: terrain type, vegetation, structures, signage, equipment, neighbouring land use. " +
    "Suggested fieldPaths: 'siteResearch.terrain', 'siteResearch.vegetation', 'siteResearch.visibleStructures'.",
  aerial:
    'This is a top-down aerial / satellite image of the project parcel. Emit findings for parcel ' +
    'shape, neighbouring infrastructure (transmission lines, roads, water bodies), and apparent ' +
    "land cover. Suggested fieldPaths: 'parcel.shape', 'grid.visibleTransmission', 'flood.visibleWater'.",
  'kmz-overlay':
    'This is a KMZ / KML map overlay (parcel boundary on a base map). Emit findings ONLY for ' +
    'boundary, area (if a scale or marker is shown), and any overlay labels. Suggested fieldPaths: ' +
    "'parcel.boundary', 'parcel.acres', 'parcel.labels'.",
  'flood-map':
    'This is a FEMA Flood Insurance Rate Map (FIRM) or NFHL crop. Emit findings for the flood zone ' +
    "designation (e.g. 'X', 'AE', 'VE'), SFHA status, and BFE when shown. Suggested fieldPaths: " +
    "'flood.floodZone', 'flood.sfha', 'flood.bfe'.",
  'survey-plat':
    'This is a recorded survey plat or boundary survey. Emit findings for parcel acreage, ' +
    "bearings, monuments, easements, recordation info. Suggested fieldPaths: 'parcel.acres', " +
    "'parcel.bearings', 'parcel.easements', 'parcel.surveyDate'.",
  'one-line-diagram':
    "This is an electrical one-line (single-line) diagram. Emit findings for visible voltages, " +
    "transformer ratings, bus tie configurations, generation sources. Suggested fieldPaths: " +
    "'grid.voltage', 'grid.transformerRatings', 'grid.busConfiguration'.",
  'protest-flyer':
    'This is a community-protest flyer indicating public opposition to a project. Emit findings ' +
    'ONLY for the protest subject, date/event details, and the asserted concerns (as quotes, not ' +
    "endorsements). Suggested fieldPaths: 'community.protestSubject', 'community.protestDate', " +
    "'community.assertedConcerns'.",
  'permit-stamp':
    'This is an agency permit stamp / approval seal. Emit findings for issuing agency, permit ' +
    "number, issue date, approval scope. Suggested fieldPaths: 'permits.agency', " +
    "'permits.permitNumber', 'permits.issueDate', 'permits.scope'.",
  'borehole-log':
    'This is a geotechnical borehole log. Emit findings for USCS soil class (top layer or ' +
    'representative), allowable bearing capacity in kPa, borehole depth in metres, depth to ' +
    "groundwater in metres, drilling date. Suggested fieldPaths: 'geotech.uscsClass', " +
    "'geotech.bearingCapacityKPa', 'geotech.boreholeDepthM', 'geotech.groundwaterDepthM'.",
  // DIM-1 — the extractedText OCR pass is the payload here; findings are
  // secondary (doc screenshots vary too widely for suggested paths).
  'document-image':
    'This is an image OF a document (screenshot, scan, or photo of a spreadsheet, table, ' +
    'letter, form, or report page). Transcribe the full legible content into `extractedText` ' +
    '— every figure, table row (one row per line), heading, and label; do not summarize. ' +
    'Emit findings ONLY for values that are clearly site-relevant and directly readable ' +
    '(e.g. a cost total, a capacity, a parcel id), quoting the source text in evidenceQuote.',
};

/* ── classifyImageKind ────────────────────────────────────────────── */

/**
 * Decode a Blob into base64 + mimeType for Gemini inlineData. Browser
 * + Node compatible: prefers `arrayBuffer()` (available on both
 * `Blob` and `File` in modern Node ≥18) and falls back to FileReader
 * when arrayBuffer isn't there (legacy jsdom).
 */
async function blobToBase64(blob: Blob): Promise<{ base64: string; mimeType: string }> {
  const mimeType = blob.type || 'application/octet-stream';
  if (typeof blob.arrayBuffer === 'function') {
    const buf = await blob.arrayBuffer();
    // Buffer is the cheap path on Node; btoa on browsers via Uint8Array.
    if (typeof Buffer !== 'undefined') {
      return { base64: Buffer.from(buf).toString('base64'), mimeType };
    }
    let binary = '';
    const bytes = new Uint8Array(buf);
    for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
    return { base64: typeof btoa === 'function' ? btoa(binary) : '', mimeType };
  }
  // FileReader fallback for very old runtimes.
  return await new Promise<{ base64: string; mimeType: string }>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve({ base64: comma >= 0 ? result.slice(comma + 1) : result, mimeType });
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Build the classifier prompt. Closed-vocabulary instruction +
 * conservative-confidence hint, so the model errs toward `'other'`
 * rather than confidently mis-labelling a screenshot as a flood map.
 */
function buildClassifierPrompt(kindHint?: ImageKind): string {
  const vocab = IMAGE_KIND_VALUES.join(', ');
  const hintLine = kindHint && kindHint !== 'other'
    ? `\nThe uploader has hinted this image may be a '${kindHint}'. Use that as a prior, but ` +
      'override the hint when the image is clearly something else.'
    : '';
  return (
    'Classify this image into one of the following kinds (verbatim, lower-case, no others):\n' +
    `[${vocab}]\n\n` +
    'Definitions:\n' +
    "- 'site-photo': ground-level photograph of land / parcel.\n" +
    "- 'aerial': top-down satellite or drone image.\n" +
    "- 'kmz-overlay': KMZ / KML map overlay (parcel boundary on a base map).\n" +
    "- 'flood-map': FEMA FIRM / NFHL flood-zone map crop.\n" +
    "- 'survey-plat': recorded survey plat / boundary survey.\n" +
    "- 'one-line-diagram': electrical one-line (single-line) diagram.\n" +
    "- 'protest-flyer': community-protest leaflet showing public opposition.\n" +
    "- 'permit-stamp': agency permit stamp / approval seal.\n" +
    "- 'borehole-log': geotechnical borehole / drill log with a layer table.\n" +
    "- 'document-image': an image OF a document — a screenshot, scan, or photograph of a " +
    'spreadsheet, table, letter, form, report page, cost estimate, or similar. Dense readable ' +
    'text or table structure is the signal. Prefer a more specific kind (flood-map, ' +
    'survey-plat, one-line-diagram, borehole-log, permit-stamp) when one applies.\n' +
    "- 'other': anything else (default when uncertain).\n\n" +
    'Be CONSERVATIVE: when uncertain between two kinds, return `other`. Confidence below 0.6 ' +
    'will be discarded by the caller, so a low confidence is safer than a confident wrong label.\n' +
    hintLine +
    '\n\nReturn JSON: { "kind": "<one of the above>", "confidence": <number 0..1> }.'
  );
}

/**
 * Classify a single image into one of the `ImageKind` values.
 *
 * Returns `{ kind: 'other', confidence: 0 }` on:
 *   - empty Blob,
 *   - unsupported mimeType (non-image),
 *   - Gemini call failure (timeout / malformed JSON / SDK error).
 * Never throws to the caller — IIA-2 batch callers must keep iterating
 * even when one image is unprocessable.
 */
export async function classifyImageKind(
  imageBlob: Blob,
  options?: { kindHint?: ImageKind; runner?: ImageGeminiRunner; cancelled?: () => boolean },
): Promise<{ kind: ImageKind; confidence: number }> {
  const cancelled = options?.cancelled ?? (() => false);
  if (cancelled()) return { kind: 'other', confidence: 0 };
  if (!imageBlob || imageBlob.size === 0) return { kind: 'other', confidence: 0 };
  // Defensive: anything not declared as image/* is treated as 'other'
  // up-front so a stray PDF blob doesn't burn a Gemini classifier call.
  if (imageBlob.type && !imageBlob.type.startsWith('image/')) {
    return { kind: 'other', confidence: 0 };
  }

  const runner = options?.runner ?? defaultMultimodalRunner;
  try {
    const { base64, mimeType } = await blobToBase64(imageBlob);
    if (!base64) return { kind: 'other', confidence: 0 };
    const raw = await runner({
      base64Data: base64,
      mimeType,
      prompt: buildClassifierPrompt(options?.kindHint),
      schema: CLASSIFIER_SCHEMA,
      timeoutMs: 30_000,
    });
    return projectClassifierResponse(raw);
  } catch (e) {
    if (!cancelled()) {
      console.warn('[analyzeImageEvidence] classifier failed:', (e as Error)?.message);
    }
    return { kind: 'other', confidence: 0 };
  }
}

/** Defensive projection of an unknown classifier payload into the
 *  narrow `{ kind, confidence }` shape. Out-of-vocabulary kind values
 *  collapse to `'other'`; out-of-range confidences clamp to [0, 1]. */
function projectClassifierResponse(raw: unknown): { kind: ImageKind; confidence: number } {
  if (!raw || typeof raw !== 'object') return { kind: 'other', confidence: 0 };
  const r = raw as Record<string, unknown>;
  const rawKind = typeof r.kind === 'string' ? r.kind.toLowerCase().trim() : '';
  const kind = (IMAGE_KIND_VALUES as readonly string[]).includes(rawKind) ? (rawKind as ImageKind) : 'other';
  const rawConf = typeof r.confidence === 'number' && Number.isFinite(r.confidence) ? r.confidence : 0;
  const confidence = Math.max(0, Math.min(1, rawConf));
  return { kind, confidence };
}

/* ── analyzeImageEvidence ─────────────────────────────────────────── */

/**
 * Build the extraction prompt for a known image kind.
 */
function buildExtractionPrompt(kind: Exclude<ImageKind, 'other'>): string {
  return (
    `Extract structured findings from this image. The image has been classified as '${kind}'.\n\n` +
    KIND_EXTRACTION_HINTS[kind] + '\n\n' +
    "For each finding emit: { fieldPath, value, confidence (0..1), evidenceQuote, pixelRegion? }.\n" +
    '- fieldPath: the dotted SED slot path (use the suggested paths above when applicable).\n' +
    '- value: the extracted value as a string (numbers / booleans should be stringified).\n' +
    '- confidence: how sure you are this finding is correct (0..1). Below 0.5 = drop.\n' +
    '- evidenceQuote: a verbatim text snippet or short caption from the image ' +
    'grounding the value (≤140 chars). If the image is purely visual, describe what you saw.\n' +
    "- pixelRegion (OPTIONAL): bounding box {x, y, w, h} of the source region in image pixels. " +
    'Omit when you cannot localise the source confidently.\n\n' +
    'Also emit `extractedText`: any text legible in the image (OCR pass), ≤4 000 chars.\n\n' +
    'Be CONSERVATIVE: only extract what is visually verifiable. Fabricating a value is worse ' +
    'than returning an empty findings array.\n\n' +
    'Return JSON: { "findings": [...], "extractedText": "..." }.'
  );
}

/**
 * Single-image analyzer entry point. Two-pass:
 *   1. classifier pass → { kind, kindConfidence },
 *   2. when kindConfidence ≥ MIN_KIND_CONFIDENCE and kind !== 'other',
 *      extractor pass → { findings, extractedText }.
 *
 * Returns `{ kind: 'other', kindConfidence, findings: [] }` whenever
 * the classifier was not at least `MIN_KIND_CONFIDENCE` confident
 * (regardless of the label) — IIA refuses to speculate on a kind it
 * isn't sure of.
 *
 * Never throws to the caller — the catch on the extractor pass
 * degrades to `findings: []` while preserving the classifier's
 * `{ kind, kindConfidence }`. IIA-2 batch callers depend on this for
 * "keep iterating" behaviour.
 *
 * @param imageBlob raw image blob (PNG / JPEG / WEBP — anything Gemini
 *                  multimodal-flash accepts inline).
 * @param kindHint  optional uploader-supplied prior; used as a soft
 *                  hint to the classifier prompt only.
 */
export async function analyzeImageEvidence(
  imageBlob: Blob,
  kindHint?: ImageKind,
  options?: { runner?: ImageGeminiRunner; cancelled?: () => boolean },
): Promise<ImageAnalysisResult> {
  const cancelled = options?.cancelled ?? (() => false);
  const runner = options?.runner ?? defaultMultimodalRunner;

  // 1) classify
  const { kind, confidence: kindConfidence } = await classifyImageKind(imageBlob, {
    kindHint,
    runner,
    cancelled,
  });

  if (kindConfidence < MIN_KIND_CONFIDENCE || kind === 'other') {
    return { kind: 'other', kindConfidence, findings: [] };
  }

  if (cancelled()) return { kind: 'other', kindConfidence, findings: [] };

  // 2) extract per-kind
  try {
    const { base64, mimeType } = await blobToBase64(imageBlob);
    if (!base64) return { kind, kindConfidence, findings: [] };
    const raw = await runner({
      base64Data: base64,
      mimeType,
      prompt: buildExtractionPrompt(kind),
      schema: EXTRACTION_RESPONSE_SCHEMA,
      timeoutMs: 30_000,
    });
    return projectExtractionResponse(raw, kind, kindConfidence);
  } catch (e) {
    if (!cancelled()) {
      console.warn('[analyzeImageEvidence] extraction failed:', (e as Error)?.message);
    }
    return { kind, kindConfidence, findings: [] };
  }
}

/* ── Result projection / defensive parsing ───────────────────────── */

/**
 * Coerce an unknown extractor payload into the strict
 * `ImageAnalysisResult` shape. Drops findings that:
 *   - have a non-string `fieldPath`,
 *   - have confidence < 0.5 (consistent with the prompt's drop
 *     threshold — Gemini's structured-output sometimes returns rows it
 *     was told to drop),
 *   - lack an `evidenceQuote`.
 * `pixelRegion` is silently stripped when not all four members are
 * finite non-negative numbers.
 */
function projectExtractionResponse(
  raw: unknown,
  kind: ImageKind,
  kindConfidence: number,
): ImageAnalysisResult {
  if (!raw || typeof raw !== 'object') {
    return { kind, kindConfidence, findings: [] };
  }
  const r = raw as Record<string, unknown>;
  const findingsRaw = Array.isArray(r.findings) ? r.findings : [];
  const findings: ImageFinding[] = [];
  for (const row of findingsRaw) {
    if (!row || typeof row !== 'object') continue;
    const f = row as Record<string, unknown>;
    const fieldPath = typeof f.fieldPath === 'string' ? f.fieldPath.trim() : '';
    if (!fieldPath) continue;
    const confidence = typeof f.confidence === 'number' && Number.isFinite(f.confidence)
      ? Math.max(0, Math.min(1, f.confidence))
      : 0;
    if (confidence < 0.5) continue;
    const evidenceQuote = typeof f.evidenceQuote === 'string' && f.evidenceQuote.trim()
      ? f.evidenceQuote.slice(0, 140)
      : '';
    if (!evidenceQuote) continue;
    const pixelRegion = projectPixelRegion(f.pixelRegion);
    findings.push({
      fieldPath,
      value: f.value,
      confidence,
      evidenceQuote,
      ...(pixelRegion ? { pixelRegion } : {}),
    });
  }
  const extractedText = typeof r.extractedText === 'string' && r.extractedText.trim()
    ? r.extractedText.slice(0, 4_000)
    : undefined;
  return {
    kind,
    kindConfidence,
    findings,
    ...(extractedText ? { extractedText } : {}),
  };
}

/** Defensive bounding-box projection. Rejects rows with non-finite
 *  numbers, negative dimensions, or zero-area regions. */
function projectPixelRegion(raw: unknown): { x: number; y: number; w: number; h: number } | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const x = typeof r.x === 'number' && Number.isFinite(r.x) ? r.x : NaN;
  const y = typeof r.y === 'number' && Number.isFinite(r.y) ? r.y : NaN;
  const w = typeof r.w === 'number' && Number.isFinite(r.w) ? r.w : NaN;
  const h = typeof r.h === 'number' && Number.isFinite(r.h) ? r.h : NaN;
  if (![x, y, w, h].every(n => Number.isFinite(n) && n >= 0)) return undefined;
  if (w === 0 || h === 0) return undefined;
  return { x, y, w, h };
}
